import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../assets/scss/MarketWatch.scss";
import { useDispatch, useSelector } from "react-redux";
import { addOrder, getLotQtyByDisplayName } from "Store/Reducer/marketwatch.slice";
import { showMessage } from "Store/Reducer/common.slice";
import { getMarginSummary } from "Store/Reducer/margin.slice";
import { getSessionData, checkIsEmpty } from "utils/common";
import { formatOrderConfirmClientCell } from "utils/tradingAlerts";
import {
  clientFreeMarginMessage,
  readFreeMargin,
  readEquity,
} from "utils/freeMarginGate";
import { store } from "Store/appStore";
import useViewport from "hooks/useViewport";

/** Same as fxbazzar_frontend `configs/routesConfig` — limit price decimal rules. */
const SCRIPT_WITHOUT_DOT = [
  "GOLD",
  "GOLDM",
  "SILVER",
  "SILVERM",
  "CRUDEOIL",
  "CRUDEOILM",
];
const SCRIPT_WITH_DOT = [
  "ALUMINI",
  "ALUMINIUM",
  "COPPER",
  "LEAD",
  "LEADMINI",
  "NATGASMINI",
  "NATURALGAS",
  "SILVERMIC",
  "ZINC",
  "ZINCMINI",
];
const SCRIPT_WITH_DOT_ACCEPT = [
  "00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60", "65", "70", "75", "80", "85", "90", "95",
];

const MCX_LIKE_SEGMENTS = [2, 3, 4];

const EXECUTION_OPTIONS = [
  { value: "market", label: "Market Execution", executionValue: 5, side: null },
  { value: "buyLimit", label: "Buy Limit", executionValue: 1, side: "BUY" },
  { value: "sellLimit", label: "Sell Limit", executionValue: 3, side: "SELL" },
  { value: "buyStop", label: "Buy Stop", executionValue: 2, side: "BUY" },
  { value: "sellStop", label: "Sell Stop", executionValue: 4, side: "SELL" },
];

/**
 * Same validation path as fxbazzar_frontend `MobileBuySellModal.handleSubmit`.
 */
function validateQuickOrder({ client, executionType, lot, qty, fPrice, live }) {
  if (
    checkIsEmpty(client) ||
    !Number(fPrice || 0) ||
    !Number(lot || 0) ||
    !Number(qty || 0)
  ) {
    return null;
  }

  const segmentId = Number(live.segmentId);
  const symbol = live.symbol || "";
  const buyPrice = Number(live.buyPrice);
  const sellPrice = Number(live.sellPrice);
  const lastTradedPrice = Number(live.lastTradedPrice);
  const ltTime = Number(live.ltTime ?? 0);
  const ltTime2 = live.ltTime2 ?? "";
  const offQuoteSecond = Number(live.offQuoteSecond ?? 0);
  const stopOutLevelType = Number(live.stopOutLevelType ?? 0);
  const stopOutLevelAmount = Number(live.stopOutLevelAmount ?? 0);

  const orderTypeVal = Number(executionType || 5);
  let isSubmit = true;
  let message = "";

  if (orderTypeVal !== 5) {
    let priceMinLimit = 0;
    let priceMaxLimit = 0;
    if (segmentId !== 3) {
      priceMinLimit = fPrice - (fPrice * 10) / 100;
      priceMaxLimit = fPrice + (fPrice * 10) / 100;
    } else {
      priceMinLimit = fPrice - (fPrice * 150) / 100;
      priceMaxLimit = fPrice + (fPrice * 150) / 100;
      if (priceMinLimit < 0) priceMinLimit = 0;
    }
    if ((segmentId === 2 && SCRIPT_WITH_DOT.includes(symbol)) || segmentId !== 2) {
      const decParts = fPrice.toString().split(".");
      const decimals = [...decParts];
      decimals[1] = decimals[1]
        ? decimals[1].length === 1
          ? decimals[1] + "0"
          : decimals[1]
        : "00";
      if ((segmentId === 2 && !SCRIPT_WITH_DOT_ACCEPT.includes(decimals[1])) || segmentId !== 2) {
        if (!SCRIPT_WITH_DOT_ACCEPT.includes(decimals[1]) && ![1, 11].includes(segmentId)) {
          message = "Ticksize must be : 0.05";
          isSubmit = false;
        }
        if (!SCRIPT_WITH_DOT_ACCEPT.includes(decimals[1]) && [1, 11].includes(segmentId) && fPrice >= 250) {
          message = "Ticksize must be : 0.05";
          isSubmit = false;
        }
      }
    }
    if (priceMinLimit > lastTradedPrice || priceMaxLimit < lastTradedPrice) {
      message = "Limit oder must be placed within circuit levels";
      isSubmit = false;
    }
    if (isSubmit) {
      let minPriceLimit;
      let maxPriceLimit = 0;
      if (stopOutLevelType === 1) {
        minPriceLimit = buyPrice - stopOutLevelAmount;
        maxPriceLimit = sellPrice + stopOutLevelAmount;
      } else {
        minPriceLimit =
          buyPrice - (stopOutLevelAmount === 0 ? stopOutLevelAmount : (buyPrice * stopOutLevelAmount) / 100);
        maxPriceLimit =
          sellPrice + (stopOutLevelAmount === 0 ? stopOutLevelAmount : (sellPrice * stopOutLevelAmount) / 100);
      }
      if (fPrice > minPriceLimit && fPrice < maxPriceLimit) {
        message = "Order cannot be placed near the current market price.";
        isSubmit = false;
      }
    }
  }

  if (isSubmit && ltTime !== 0 && offQuoteSecond !== 0) {
    const parts = String(ltTime2 || "").split(":");
    if (parts.length >= 3) {
      const lastTime = new Date();
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parseInt(parts[2], 10);
      if (!Number.isNaN(h) && !Number.isNaN(m) && !Number.isNaN(s)) {
        lastTime.setHours(h, m, s, 0);
        const diffTime = parseInt((new Date() - lastTime) / 1000, 10);
        if (offQuoteSecond < diffTime) {
          message = "Off Quote";
          isSubmit = false;
        }
      }
    }
  }
  if (isSubmit && buyPrice >= sellPrice) {
    message = "Wrong Quote";
    isSubmit = false;
  }

  if (!isSubmit && message) return message;
  return "";
}

function lotStringFromQty(qtyStr, perLotQty) {
  const q = parseFloat(qtyStr);
  if (Number.isNaN(q) || q < 0) return "0";
  const per = perLotQty >= 1 ? perLotQty : 1;
  const l = Math.round((q / per) * 100) / 100;
  return String(Math.max(0, l));
}

function roundLotInput(s) {
  const raw = String(s ?? "").trim();
  const l = parseFloat(raw);
  if (Number.isNaN(l) || l < 0) return "0";
  return String(Math.round(l * 100) / 100);
}

function normalizeLiveRow(row) {
  if (!row || typeof row !== "object") return null;
  const ltp = Number(row.lastTradedPrice ?? row.ltp ?? 0);
  const bid = Number(row.buyPrice ?? row.bid ?? 0);
  const ask = Number(row.sellPrice ?? row.ask ?? 0);
  const high = Number(row.high ?? 0);
  const low = Number(row.low ?? 0);
  const perLotQty = Number(row.qty) > 0 ? Number(row.qty) : 1;
  return {
    ...row,
    script: row.script || row.displayName || row.symbol || "",
    displayName: row.displayName || row.script || row.symbol || "",
    symbol: row.symbol || row.script || row.displayName || "",
    segmentId: row.segmentId != null ? Number(row.segmentId) : 1,
    lastTradedPrice: ltp,
    buyPrice: bid,
    sellPrice: ask,
    high,
    low,
    lot: Number(row.lot) > 0 ? Number(row.lot) : 1,
    qty: perLotQty,
    close: Number(row.close ?? row.previousClose ?? 0),
    netQty: row.netQty ?? 0,
    ltTime: row.ltTime ?? 0,
    ltTime2: row.ltTime2 ?? "",
    offQuoteSecond: row.offQuoteSecond ?? 0,
    stopOutLevelAmount: row.stopOutLevelAmount ?? 0,
    stopOutLevelType: row.stopOutLevelType ?? 0,
  };
}

function resolveModePrice(mode, side, bid, ask, ltp) {
  const isLtpMode = Number(mode) === 1;
  if (isLtpMode) return Number(ltp || 0);
  return side === "BUY" ? Number(ask || 0) : Number(bid || 0);
}

function resolveAccountPriceModeFromSession(s, live) {
  const liveCandidates = [
    live?.positionDataType,
    live?.PositionDataType,
    live?.mobileInformation?.positionDataType,
    live?.mobileInformation?.PositionDataType,
  ];
  for (const c of liveCandidates) {
    const n = Number(c);
    if (n === 1 || n === 2) return n;
    const t = String(c ?? "").trim().toLowerCase();
    if (t === "ltpwise" || t === "ltp" || t === "1") return 1;
    if (t === "bidaskwise" || t === "bid/ask" || t === "bidask" || t === "2") return 2;
  }
  if (!s || typeof s !== "object") return 2;
  const candidates = [
    s.positionDataType,
    s.PositionDataType,
    s?.mobileInformation?.positionDataType,
    s?.mobileInformation?.PositionDataType,
    s?.MobileInformation?.positionDataType,
    s?.MobileInformation?.PositionDataType,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (n === 1 || n === 2) return n;
    const t = String(c ?? "").trim().toLowerCase();
    if (t === "ltpwise" || t === "ltp" || t === "1") return 1;
    if (t === "bidaskwise" || t === "bid/ask" || t === "bidask" || t === "2") return 2;
  }
  return 2;
}

const MAX_MAP_SCAN = 160;

/**
 * Merge prop row with latest Redux watchlist row (O(1) key lookups first).
 * Avoids scanning the whole Map on every socket tick when the modal only cares about one symbol.
 */
export function resolveFreshWatchRow(row, marketWatchList) {
  if (!row || typeof row !== "object") return row;
  if (!marketWatchList || !(marketWatchList instanceof Map) || marketWatchList.size === 0) return row;
  const tryKeys = [];
  if (row.id != null && row.id !== "") tryKeys.push(`id:${row.id}`);
  for (const k of [row.displayName, row.script, row.symbol, row.name].filter(Boolean)) {
    tryKeys.push(String(k));
  }
  for (const k of tryKeys) {
    if (marketWatchList.has(k)) return { ...row, ...marketWatchList.get(k) };
  }
  if (marketWatchList.size > MAX_MAP_SCAN) return row;
  for (const v of marketWatchList.values()) {
    if (!v || typeof v !== "object") continue;
    const sameId = row.id != null && row.id !== "" && v.id === row.id;
    const sameName =
      (row.displayName && v.displayName === row.displayName) ||
      (row.script && v.script === row.script) ||
      (row.symbol && v.symbol === row.symbol);
    if (sameId || sameName) return { ...row, ...v };
  }
  return row;
}

/** Re-render quick-order only when this symbol’s live quote fields change — not on every other row’s tick. */
function quickOrderRowQuoteEqual(next, prev) {
  if (next === prev) return true;
  if (next == null && prev == null) return true;
  if (!next || !prev) return false;
  if (String(next.id ?? "") !== String(prev.id ?? "")) return false;
  const nk = `${next.displayName}|${next.script}|${next.symbol}`;
  const pk = `${prev.displayName}|${prev.script}|${prev.symbol}`;
  if (nk !== pk) return false;
  return (
    Number(next.lastTradedPrice) === Number(prev.lastTradedPrice) &&
    Number(next.buyPrice) === Number(prev.buyPrice) &&
    Number(next.sellPrice) === Number(prev.sellPrice) &&
    Number(next.high) === Number(prev.high) &&
    Number(next.low) === Number(prev.low) &&
    Number(next.open) === Number(prev.open) &&
    Number(next.volume ?? next.Volume) === Number(prev.volume ?? prev.Volume) &&
    Number(next.ltTime) === Number(prev.ltTime) &&
    String(next.ltTime2 ?? "") === String(prev.ltTime2 ?? "") &&
    Number(next.offQuoteSecond) === Number(prev.offQuoteSecond) &&
    Number(next.stopOutLevelType) === Number(prev.stopOutLevelType) &&
    Number(next.stopOutLevelAmount) === Number(prev.stopOutLevelAmount) &&
    Number(next.segmentId) === Number(prev.segmentId) &&
    Number(next.netQty) === Number(prev.netQty) &&
    Number(next.qty) === Number(prev.qty) &&
    Number(next.lot) === Number(prev.lot) &&
    Number(next.close ?? next.previousClose) === Number(prev.close ?? prev.previousClose)
  );
}

/** Flush to the chart pane’s left edge (drawing tool rail is a sibling outside this pane). */
const ORDER_DOCK_EDGE_PAD = 0;

/**
 * Legacy fxbazzar_frontend `MobileBuySellModal` behaviour with new MetaTrader-style UI.
 */
export default function MarketWatchQuickOrderModal({
  open,
  onClose,
  row,
  docked = true,
  menuOpen = false,
  isMobile = false,
  darkTheme = false,
}) {
  const dispatch = useDispatch();
  const { isMobile: isMobileViewport } = useViewport();
  const showMobileSheet = isMobile || isMobileViewport;
  const isAddedOrderLoading = useSelector((s) => s.marketWatch?.isAddedOrderLoading);
  const marginSummary = useSelector((s) => s.margin?.marginSummary);
  const openPositions = useSelector((s) => s.positionReport?.duplicatePositionReportList || []);
  const freeMarginLabel = useMemo(() => {
    const free = readFreeMargin(marginSummary);
    return free == null ? "—" : free.toFixed(2);
  }, [marginSummary]);
  const equityLabel = useMemo(() => {
    const equity = readEquity(marginSummary);
    return equity == null ? "—" : equity.toFixed(2);
  }, [marginSummary]);

  const rowIdentityKey = useMemo(() => {
    if (!row || typeof row !== "object") return "";
    return [row.segmentId, row.id, row.displayName, row.script, row.symbol]
      .filter((x) => x != null && x !== "")
      .join("|");
  }, [row]);
  const rowRef = useRef(row);
  rowRef.current = row;

  const selectMergedRow = useCallback(
    (state) => resolveFreshWatchRow(rowRef.current, state.marketWatch?.marketWatchList),
    [rowIdentityKey]
  );
  const freshRow = useSelector(selectMergedRow, quickOrderRowQuoteEqual);

  /** Move docked panel by dragging the top handle (mouse + touch). */
  const [dockOffset, setDockOffset] = useState({ x: 0, y: 0 });
  const dockDragRef = useRef(null);
  const dockAsideRef = useRef(null);

  useEffect(() => {
    if (!open) setDockOffset({ x: 0, y: 0 });
  }, [open]);

  /** Default dock flush to the left start of the chart pane (rail is a sibling). */
  const prevDockedOpenRef = useRef(false);
  useLayoutEffect(() => {
    if (!docked || !open || showMobileSheet) {
      prevDockedOpenRef.current = false;
      return;
    }
    const asideEl = dockAsideRef.current;
    const host = asideEl?.closest?.(".fx-chart-pane") ?? asideEl?.closest?.("main");
    if (!asideEl || !host) return;
    const w = host.getBoundingClientRect().width;
    const h = host.getBoundingClientRect().height;
    const pw = asideEl.getBoundingClientRect().width || 272;
    const ph = asideEl.getBoundingClientRect().height || 280;
    const pad = ORDER_DOCK_EDGE_PAD;
    const maxX = Math.max(pad, w - pw - pad);
    const maxY = Math.max(pad, h - ph - pad);
    const minX = pad;
    if (!prevDockedOpenRef.current) {
      prevDockedOpenRef.current = true;
      setDockOffset({ x: minX, y: 0 });
    } else {
      setDockOffset((prev) => ({
        x: Math.min(maxX, Math.max(minX, prev.x)),
        y: Math.min(maxY, Math.max(0, prev.y)),
      }));
    }
  }, [docked, open, showMobileSheet, menuOpen]);

  useEffect(() => {
    if (!docked || !open || showMobileSheet) return undefined;
    const reclamp = () => {
      setDockOffset((prev) => {
        const asideEl = dockAsideRef.current;
        const host = asideEl?.closest?.(".fx-chart-pane") ?? asideEl?.closest?.("main");
        if (!asideEl || !host) return prev;
        const r = host.getBoundingClientRect();
        const ar = asideEl.getBoundingClientRect();
        const w = r.width;
        const h = r.height;
        const pw = ar.width || 272;
        const ph = ar.height || 280;
        const pad = ORDER_DOCK_EDGE_PAD;
        const maxX = Math.max(pad, w - pw - pad);
        const maxY = Math.max(pad, h - ph - pad);
        const minX = pad;
        return {
          x: Math.min(maxX, Math.max(minX, prev.x)),
          y: Math.min(maxY, Math.max(0, prev.y)),
        };
      });
    };
    window.addEventListener("resize", reclamp);
    window.addEventListener("fx-chart-layout", reclamp);
    return () => {
      window.removeEventListener("resize", reclamp);
      window.removeEventListener("fx-chart-layout", reclamp);
    };
  }, [docked, open, showMobileSheet]);

  /** Inline confirm (no SweetAlert2) — same idea as fxbazzar_frontend `window.confirm`, instant open. */
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [doneInfo, setDoneInfo] = useState(null);
  useEffect(() => {
    if (!open) {
      setPendingConfirm(null);
      setDoneInfo(null);
    }
  }, [open]);

  useEffect(() => {
    if (!pendingConfirm && !doneInfo) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || isAddedOrderLoading) return;
      if (doneInfo) setDoneInfo(null);
      else setPendingConfirm(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingConfirm, doneInfo, isAddedOrderLoading]);

  useEffect(() => {
    if (!docked || showMobileSheet) return undefined;
    const clamp = (nx, ny) => {
      const pad = ORDER_DOCK_EDGE_PAD;
      const asideEl = dockAsideRef.current;
      const host = asideEl?.closest?.(".fx-chart-pane") ?? asideEl?.closest?.("main");
      const hostRect = host?.getBoundingClientRect?.();
      const asideRect = asideEl?.getBoundingClientRect?.();
      const w = hostRect?.width ?? (typeof window !== "undefined" ? window.innerWidth : 800);
      const h = hostRect?.height ?? (typeof window !== "undefined" ? window.innerHeight : 600);
      const panelW = asideRect?.width ?? 272;
      const panelH = asideRect?.height ?? 280;
      const maxX = Math.max(pad, w - panelW - pad);
      const maxY = Math.max(pad, h - panelH - pad);
      const minX = pad;
      return {
        x: Math.min(maxX, Math.max(minX, nx)),
        y: Math.min(maxY, Math.max(0, ny)),
      };
    };
    const onMove = (e) => {
      const d = dockDragRef.current;
      if (!d) return;
      const cx = e.clientX ?? e.touches?.[0]?.clientX;
      const cy = e.clientY ?? e.touches?.[0]?.clientY;
      if (cx == null || cy == null) return;
      setDockOffset(clamp(d.origX + (cx - d.startX), d.origY + (cy - d.startY)));
    };
    const onUp = () => {
      dockDragRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [docked, showMobileSheet]);

  const onDockDragStart = useCallback(
    (e) => {
      if (!docked) return;
      if (e.type === "mousedown" && e.button !== 0) return;
      e.preventDefault();
      const cx = e.clientX ?? e.touches?.[0]?.clientX;
      const cy = e.clientY ?? e.touches?.[0]?.clientY;
      if (cx == null || cy == null) return;
      dockDragRef.current = {
        startX: cx,
        startY: cy,
        origX: dockOffset.x,
        origY: dockOffset.y,
      };
    },
    [docked, dockOffset.x, dockOffset.y]
  );

  const live = useMemo(() => normalizeLiveRow(freshRow), [freshRow]);
  const liveRef = useRef(live);
  liveRef.current = live;

  const [executionMode, setExecutionMode] = useState("market");
  const [showExecMenu, setShowExecMenu] = useState(false);
  /** Same as legacy `MobileBuySellModal`: both lot & qty shown; MCX-like edits lot, others edit qty. */
  const [lotField, setLotField] = useState("1");
  const [qtyField, setQtyField] = useState("1");
  const [price, setPrice] = useState("");
  /** Live contract size from Scripts (overrides stale MarketWatch row snapshot). */
  const [perLotQty, setPerLotQty] = useState(null);

  const segmentId = live ? Number(live.segmentId) : 1;
  const mcxLike = MCX_LIKE_SEGMENTS.includes(segmentId);
  const perLot = perLotQty != null && perLotQty >= 1 ? perLotQty : live && live.qty >= 1 ? live.qty : 1;
  const lotStep = 0.01;
  const symbolUpper = (live?.symbol || "").toUpperCase();
  const lotStepBtn = mcxLike ? 0.1 : 0.01;

  useEffect(() => {
    if (!open || !rowIdentityKey) return;
    setPerLotQty(null);
    const cur = liveRef.current;
    if (!cur?.displayName || cur.segmentId == null) return;
    let cancelled = false;
    dispatch(
      getLotQtyByDisplayName({
        segmentId: cur.segmentId,
        displayname: cur.displayName,
      })
    ).then((action) => {
      if (cancelled || !getLotQtyByDisplayName.fulfilled.match(action)) return;
      const q = Number(action.payload?.qty ?? action.payload?.Qty);
      if (q > 0) setPerLotQty(q);
    });
    return () => {
      cancelled = true;
    };
  }, [open, rowIdentityKey, dispatch]);

  useEffect(() => {
    if (!open || !rowIdentityKey) return;
    const cur = liveRef.current;
    if (!cur) return;
    const per = perLotQty != null && perLotQty >= 1 ? perLotQty : cur.qty >= 1 ? cur.qty : 1;
    if (mcxLike) {
      const l = cur.lot >= 1 ? String(cur.lot) : "1";
      const lot = roundLotInput(l);
      setLotField(lot);
      setQtyField(String(Math.max(0, Math.round(Number(lot) * per))));
    } else {
      const l = cur.lot >= 1 ? String(cur.lot) : "1";
      const q = cur.qty >= 1 ? String(Math.round(Number(l) * per)) : String(per);
      setQtyField(q);
      setLotField(lotStringFromQty(q, per));
    }
    setPrice(String(cur.lastTradedPrice || ""));
    setExecutionMode("market");
    setShowExecMenu(false);
  }, [rowIdentityKey, open, mcxLike, perLotQty]);

  useEffect(() => {
    if (!showExecMenu) return undefined;
    const onCloseMenu = (e) => {
      const target = e.target;
      if (target && typeof target.closest === "function" && target.closest(".mw-mt5__exec-dropdown")) return;
      setShowExecMenu(false);
    };
    document.addEventListener("pointerdown", onCloseMenu);
    return () => document.removeEventListener("pointerdown", onCloseMenu);
  }, [showExecMenu]);

  const client = useMemo(() => {
    const s = getSessionData() || {};
    const id = s.userId ?? s.UserId ?? "";
    const name = s.userName ?? s.UserName ?? "User";
    return { label: name, value: id };
  }, [open]);
  const accountPriceMode = useMemo(() => {
    const s = getSessionData() || {};
    return resolveAccountPriceModeFromSession(s, live);
  }, [open, live]);

  const { lotStr, qtyStr } = useMemo(() => {
    if (!live) return { lotStr: "0", qtyStr: "0" };
    if (mcxLike) {
      const lot = roundLotInput(lotField);
      const qn = Math.round(Number(lot) * perLot);
      return { lotStr: lot, qtyStr: String(Math.max(0, qn)) };
    }
    const q = String(qtyField ?? "").trim();
    return { lotStr: lotStringFromQty(q, perLot), qtyStr: q };
  }, [lotField, qtyField, live, mcxLike, perLot]);

  const selectedExecution = useMemo(
    () => EXECUTION_OPTIONS.find((o) => o.value === executionMode) || EXECUTION_OPTIONS[0],
    [executionMode]
  );

  const isAllowBuySell = useMemo(() => {
    if (selectedExecution.executionValue === 5) return false;
    const fPrice = Number(price);
    if (Number.isNaN(fPrice)) return false;
    const buyPrice = Number(live?.buyPrice);
    const sellPrice = Number(live?.sellPrice);
    const stopOutLevelType = Number(live?.stopOutLevelType ?? 0);
    const stopOutLevelAmount = Number(live?.stopOutLevelAmount ?? 0);
    let minPriceLimit;
    let maxPriceLimit = 0;
    if (stopOutLevelType === 1) {
      minPriceLimit = buyPrice - stopOutLevelAmount;
      maxPriceLimit = sellPrice + stopOutLevelAmount;
    } else {
      minPriceLimit =
        buyPrice - (stopOutLevelAmount === 0 ? stopOutLevelAmount : (buyPrice * stopOutLevelAmount) / 100);
      maxPriceLimit =
        sellPrice + (stopOutLevelAmount === 0 ? stopOutLevelAmount : (sellPrice * stopOutLevelAmount) / 100);
    }
    return (
      (fPrice > minPriceLimit && fPrice < maxPriceLimit) ||
      (fPrice >= buyPrice && fPrice <= sellPrice)
    );
  }, [selectedExecution, price, live]);

  const marketSellNowPrice = useMemo(
    () => resolveModePrice(accountPriceMode, "SELL", live?.buyPrice, live?.sellPrice, live?.lastTradedPrice),
    [accountPriceMode, live]
  );
  const marketBuyNowPrice = useMemo(
    () => resolveModePrice(accountPriceMode, "BUY", live?.buyPrice, live?.sellPrice, live?.lastTradedPrice),
    [accountPriceMode, live]
  );
  const marketPriceHint = accountPriceMode === 1 ? "LTP" : null;

  /** Latest form + live snapshot — submit() stays stable so Buy/Sell aren’t recreated every tick. */
  const submitSnapRef = useRef({});
  submitSnapRef.current = {
    live,
    lotStr,
    qtyStr,
    client,
    executionMode,
    selectedExecution,
    price,
  };

  const startOrderConfirm = useCallback(
    async (side) => {
      const snap = submitSnapRef.current;
      const L = snap.live;
      if (!L) return;
      const exec = snap.selectedExecution || EXECUTION_OPTIONS[0];
      const finalSide = exec.side || side;
      const p = exec.executionValue === 5
        ? resolveModePrice(
            accountPriceMode,
            finalSide,
            Number(L.buyPrice || 0),
            Number(L.sellPrice || 0),
            Number(L.lastTradedPrice || 0)
          )
        : Number(snap.price);
      const fPrice = parseFloat(String(p));

      const v = validateQuickOrder({
        client: snap.client,
        executionType: exec.executionValue,
        lot: snap.lotStr,
        qty: snap.qtyStr,
        fPrice,
        live: L,
      });
      if (v === null) return;
      if (v) {
        dispatch(showMessage({ message: v }));
        return;
      }

      // Refresh margin, then early Free-Margin gate (closes still allowed).
      try {
        await dispatch(getMarginSummary());
      } catch {
        // Server remains source of truth if summary refresh fails.
      }
      const latestSummary = store.getState()?.margin?.marginSummary || marginSummary;
      const positions =
        store.getState()?.positionReport?.duplicatePositionReportList || openPositions;
      const early = clientFreeMarginMessage({
        summary: latestSummary,
        positions,
        liveRow: L,
        side: finalSide,
      });
      if (early) {
        dispatch(showMessage({ message: early }));
        return;
      }

      setPendingConfirm({
        side: finalSide,
        lotStr: snap.lotStr,
        qtyStr: snap.qtyStr,
        fPrice,
        executionValue: exec.executionValue,
        client: snap.client,
        orderTypeLabel: exec.label,
      });
    },
    [dispatch, accountPriceMode, marginSummary, openPositions]
  );

  const cancelOrderConfirm = useCallback(() => setPendingConfirm(null), []);

  const executeOrderFromConfirm = useCallback(async () => {
    const p = pendingConfirm;
    if (!p) return;
    const L = liveRef.current;
    if (!L) return;

    try {
      await dispatch(getMarginSummary());
    } catch {
      // ignore — server enforces
    }
    const latestSummary = store.getState()?.margin?.marginSummary || marginSummary;
    const positions =
      store.getState()?.positionReport?.duplicatePositionReportList || openPositions;
    const early = clientFreeMarginMessage({
      summary: latestSummary,
      positions,
      liveRow: L,
      side: p.side,
    });
    if (early) {
      dispatch(showMessage({ message: early }));
      return;
    }

    const data = {
      client: p.client,
      buySell: p.side,
      lot: p.lotStr,
      qty: p.qtyStr,
      price: p.fPrice,
      market: { value: Number(p.executionValue || 5) },
    };

    const action = await dispatch(addOrder({ data, liveDataItem: L, isAppView: true }));
    if (action.meta.requestStatus === "fulfilled" && action.payload?.status === 200) {
      const orderLabel = String(p.orderTypeLabel || "Order").toLowerCase();
      const symbolLabel = String(L.script || L.displayName || L.symbol || "").trim();
      setPendingConfirm(null);
      dispatch(getMarginSummary());
      if (Number(p.executionValue || 5) === 5) {
        onClose?.();
        return;
      }
      setDoneInfo({
        side: p.side,
        orderLabel,
        qty: p.qtyStr,
        symbolLabel,
        price: Number.isFinite(Number(p.fPrice)) ? String(p.fPrice) : "—",
      });
    }
  }, [pendingConfirm, dispatch, marginSummary, openPositions, onClose]);

  const bumpLot = useCallback(
    (delta) => {
      const n = Math.max(mcxLike ? 0.01 : 0, (parseFloat(lotField) || 0) + delta);
      const lot = String(Math.round(n * 100) / 100);
      setLotField(lot);
      setQtyField(String(Math.max(0, Math.round(Number(lot) * perLot))));
    },
    [lotField, perLot, mcxLike]
  );

  const bumpQty = useCallback(
    (delta) => {
      const n = Math.max(0, Math.round((parseFloat(qtyField) || 0) + delta));
      const q = String(n);
      setQtyField(q);
      setLotField(lotStringFromQty(q, perLot));
    },
    [qtyField, perLot]
  );

  const onLotChange = useCallback(
    (e) => {
      const v = e.target.value;
      setLotField(v);
      const l = parseFloat(v);
      if (!Number.isNaN(l) && l >= 0) {
        const lot = roundLotInput(v);
        setQtyField(String(Math.max(0, Math.round(Number(lot) * perLot))));
      }
    },
    [perLot]
  );

  const onQtyChange = useCallback(
    (e) => {
      const v = e.target.value;
      if (Number(v) >= 0 && String(v).length <= 15) {
        setQtyField(v);
        setLotField(lotStringFromQty(v, perLot));
      }
    },
    [perLot]
  );

  const bumpPrice = useCallback((delta) => {
    const n = (parseFloat(price) || 0) + delta;
    setPrice(String(Math.round(n * 100) / 100));
  }, [price]);

  const fmtPrice = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return String(n);
  };

  const onPriceKeyDown = useCallback(
    (e) => {
      if (
        SCRIPT_WITHOUT_DOT.includes(symbolUpper) &&
        (e.key === "." || e.key === "-" || e.key === "+" || e.key === "*")
      ) {
        e.preventDefault();
      }
    },
    [symbolUpper]
  );

  /** Legacy lot field key filtering. */
  const onLotKeyDown = useCallback(
    (e) => {
      if (["-", "+", "*", "/"].includes(e.key)) e.preventDefault();
      if (mcxLike && (segmentId === 3 || segmentId === 4) && e.key === ".") e.preventDefault();
    },
    [mcxLike, segmentId]
  );

  /** Legacy qty field: digits only. */
  const onQtyKeyDown = useCallback((e) => {
    const allowedNav = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "Home", "End"];
    if (allowedNav.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault();
  }, []);

  if (!open || !live) return null;

  const bid = live.buyPrice;
  const ask = live.sellPrice;
  const ltp = live.lastTradedPrice;

  const confirmPriceStr =
    pendingConfirm && Number.isFinite(pendingConfirm.fPrice) ? String(pendingConfirm.fPrice) : "—";
  const confirmClientStr = pendingConfirm ? formatOrderConfirmClientCell(pendingConfirm.client) : "—";

  const confirmPortal =
    pendingConfirm && typeof document !== "undefined"
      ? createPortal(
          <div className="mw-confirm-pop">
            <div
              className="mw-confirm-pop__backdrop"
              aria-hidden
              onClick={() => {
                if (!isAddedOrderLoading) cancelOrderConfirm();
              }}
            />
            <div
              className="mw-confirm-pop__card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mw-confirm-order-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mw-confirm-pop__head">
                <h2 id="mw-confirm-order-title" className="mw-confirm-pop__title">
                  Confirm Order
                </h2>
                <button
                  type="button"
                  className="mw-confirm-pop__close"
                  onClick={cancelOrderConfirm}
                  disabled={isAddedOrderLoading}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="mw-confirm-pop__body">
                <table className="mw-confirm-pop__grid" role="presentation">
                  <tbody>
                    <tr>
                      <td>Lot</td>
                      <td>{pendingConfirm.lotStr}</td>
                    </tr>
                    <tr>
                      <td>Qty</td>
                      <td>{pendingConfirm.qtyStr}</td>
                    </tr>
                    <tr>
                      <td>Price</td>
                      <td>{confirmPriceStr}</td>
                    </tr>
                    <tr>
                      <td>Client</td>
                      <td>{confirmClientStr}</td>
                    </tr>
                    <tr>
                      <td>Order Type</td>
                      <td className="mw-confirm-pop__type-cell">
                        <span className="mw-confirm-pop__type">
                          {pendingConfirm.orderTypeLabel} · <strong>{pendingConfirm.side}</strong>
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>Equity</td>
                      <td>{equityLabel}</td>
                    </tr>
                    <tr>
                      <td>Free Margin</td>
                      <td>{freeMarginLabel}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {isAddedOrderLoading && (
                <div className="mw-confirm-pop__loading">Placing order…</div>
              )}
              <div className="mw-confirm-pop__footer">
                <button
                  type="button"
                  className="mw-confirm-pop__btn mw-confirm-pop__btn--cancel"
                  disabled={isAddedOrderLoading}
                  onClick={cancelOrderConfirm}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mw-confirm-pop__btn mw-confirm-pop__btn--place"
                  disabled={isAddedOrderLoading}
                  onClick={executeOrderFromConfirm}
                >
                  Place Order
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  const donePortal =
    doneInfo && typeof document !== "undefined"
      ? createPortal(
          <div className="mw-order-done-pop">
            <div
              className="mw-order-done-pop__backdrop"
              aria-hidden
              onClick={() => {
                if (!isAddedOrderLoading) setDoneInfo(null);
              }}
            />
            <div
              className="mw-order-done-pop__card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mw-order-done-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mw-order-done-pop__title" id="mw-order-done-title">
                Place order
              </div>
              <button
                type="button"
                className="mw-order-done-pop__close"
                aria-label="Close"
                onClick={() => setDoneInfo(null)}
              >
                ×
              </button>
              <div className="mw-order-done-pop__body">
                <div className="mw-order-done-pop__line">
                  <strong>{doneInfo.side?.toLowerCase()}</strong> {doneInfo.orderLabel} {doneInfo.qty}
                </div>
                <div className="mw-order-done-pop__line">
                  {doneInfo.symbolLabel} at {doneInfo.price}
                </div>
                <div className="mw-order-done-pop__status">Done</div>
              </div>
              <button
                type="button"
                className="mw-order-done-pop__ok"
                onClick={() => {
                  setDoneInfo(null);
                  onClose?.();
                }}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  const execDropdown = (
    <div className="mw-mt5__exec-dropdown">
      <button
        type="button"
        className="mw-mt5__exec-select"
        aria-label="Order type"
        aria-expanded={showExecMenu ? "true" : "false"}
        onClick={(e) => {
          e.stopPropagation();
          setShowExecMenu((v) => !v);
        }}
      >
        <span>{selectedExecution.label}</span>
        <span aria-hidden>▾</span>
      </button>
      {showExecMenu && (
        <div className="mw-mt5__exec-menu" role="listbox" onClick={(e) => e.stopPropagation()}>
          {EXECUTION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={executionMode === opt.value ? "true" : "false"}
              className={`mw-mt5__exec-menu-item${executionMode === opt.value ? " is-active" : ""}`}
              onClick={() => {
                setExecutionMode(opt.value);
                setShowExecMenu(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const themeClass = darkTheme ? " mw-order-modal--theme-dark" : "";

  const panel = (
    <div
      className={`mw-order-modal mw-order-modal--mt5${docked && !showMobileSheet ? " mw-order-modal--docked" : ""}${showMobileSheet ? " mw-order-modal--mobile-sheet" : ""}${themeClass}`}
      onClick={docked || showMobileSheet ? undefined : (e) => e.stopPropagation()}
    >
      {showMobileSheet ? (
        <>
          <div className="mw-mt5__sheet-topbar">
            <h2 className="mw-mt5__sheet-symbol">{live.script || live.displayName}</h2>
            <button type="button" className="mw-mt5__close-x" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <div className="mw-mt5__sheet-exec">{execDropdown}</div>
          <div className="mw-mt5__sheet-ltp" aria-label="Last traded price">
            LTP <strong>{fmtPrice(ltp)}</strong>
          </div>
        </>
      ) : (
        <>
          <div className="mw-mt5__top">
            {execDropdown}
            <button type="button" className="mw-mt5__close-x" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <div className="mw-mt5__symbol">
            <span className="mw-mt5__symbol-name">{live.script || live.displayName}</span>
            <span className="mw-mt5__symbol-ltp">{fmtPrice(ltp)}</span>
          </div>
        </>
      )}

      <div className="mw-mt5__lotqty" role="group" aria-label="Lot and quantity">
        <div className="mw-mt5__lotqty-col">
          <div className="mw-mt5__label">Lot</div>
          <div className="mw-mt5__stepper">
            <button
              type="button"
              className="mw-mt5__step-btn"
              onClick={() => bumpLot(-lotStepBtn)}
              aria-label="Decrease lot"
            >
              −
            </button>
            <input
              className="mw-mt5__step-input"
              type="text"
              inputMode="decimal"
              value={lotField}
              onChange={onLotChange}
              onKeyDown={onLotKeyDown}
            />
            <button
              type="button"
              className="mw-mt5__step-btn"
              onClick={() => bumpLot(lotStepBtn)}
              aria-label="Increase lot"
            >
              +
            </button>
          </div>
        </div>
        <div className="mw-mt5__lotqty-col">
          <div className="mw-mt5__label">Qty</div>
          <div className={`mw-mt5__stepper${mcxLike ? " mw-mt5__stepper--muted" : ""}`}>
            <button
              type="button"
              className="mw-mt5__step-btn"
              disabled={mcxLike}
              onClick={() => bumpQty(-1)}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <input
              className="mw-mt5__step-input"
              type="text"
              inputMode="numeric"
              disabled={mcxLike}
              value={qtyField}
              onChange={onQtyChange}
              onKeyDown={onQtyKeyDown}
            />
            <button
              type="button"
              className="mw-mt5__step-btn"
              disabled={mcxLike}
              onClick={() => bumpQty(1)}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {selectedExecution.executionValue !== 5 && (
        <div className="mw-mt5__section">
          <div className="mw-mt5__label">Price</div>
          <div className="mw-mt5__stepper">
            <button type="button" className="mw-mt5__step-btn" onClick={() => bumpPrice(-lotStep)} aria-label="Decrease price">
              −
            </button>
            <input
              className="mw-mt5__step-input"
              type="text"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onKeyDown={onPriceKeyDown}
            />
            <button type="button" className="mw-mt5__step-btn" onClick={() => bumpPrice(lotStep)} aria-label="Increase price">
              +
            </button>
          </div>
        </div>
      )}

      <div className="mw-mt5__quotes">
        <div className="mw-mt5__quote mw-mt5__quote--sell">
          <span className="mw-mt5__quote-title">Sell</span>
          <span className="mw-mt5__quote-price">{fmtPrice(marketSellNowPrice)}</span>
          <span className="mw-mt5__quote-hint">{marketPriceHint || "Bid"}</span>
        </div>
        <div className="mw-mt5__quote mw-mt5__quote--buy">
          <span className="mw-mt5__quote-title">Buy</span>
          <span className="mw-mt5__quote-price">{fmtPrice(marketBuyNowPrice)}</span>
          <span className="mw-mt5__quote-hint">{marketPriceHint || "Ask"}</span>
        </div>
      </div>

      {isAddedOrderLoading && <div className="mw-mt5__loading">Placing order…</div>}

      <div className="mw-mt5__actions">
        {selectedExecution.executionValue === 5 ? (
          <>
            <button
              type="button"
              className="mw-mt5__btn mw-mt5__btn--sell"
              disabled={isAddedOrderLoading || selectedExecution.side === "BUY"}
              onClick={() => startOrderConfirm("SELL")}
            >
              Sell Now @<span className="mw-mt5__btn-price">{fmtPrice(marketSellNowPrice)}</span>
            </button>
            <button
              type="button"
              className="mw-mt5__btn mw-mt5__btn--buy"
              disabled={isAddedOrderLoading || selectedExecution.side === "SELL"}
              onClick={() => startOrderConfirm("BUY")}
            >
              Buy Now @<span className="mw-mt5__btn-price">{fmtPrice(marketBuyNowPrice)}</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="mw-mt5__btn mw-mt5__btn--place"
            disabled={isAddedOrderLoading}
            onClick={() => startOrderConfirm(selectedExecution.side || "BUY")}
          >
            Place Order
          </button>
        )}
      </div>
    </div>
  );

  if (showMobileSheet && typeof document !== "undefined") {
    return createPortal(
      <>
        <div className={`mw-order-sheet-host${darkTheme ? " mw-order-sheet-host--dark" : ""}`}>
          <button
            type="button"
            className="mw-order-sheet-backdrop"
            aria-label="Close order panel"
            onClick={onClose}
          />
          <div
            className={`mw-order-sheet mw-order-sheet--full${darkTheme ? " mw-order-sheet--dark" : ""}`}
            role="dialog"
            aria-label="New order"
          >
            {panel}
          </div>
        </div>
        {confirmPortal}
        {donePortal}
      </>,
      document.body
    );
  }

  if (docked) {
    return (
      <>
        <aside
          ref={dockAsideRef}
          className="order-dock-aside"
          aria-label="New order"
          style={{ transform: `translate(${dockOffset.x}px, ${dockOffset.y}px)` }}
        >
          <div
            className="order-dock-drag-handle"
            role="separator"
            aria-label="Drag to move order panel"
            onMouseDown={onDockDragStart}
            onTouchStart={onDockDragStart}
          >
            <span className="order-dock-drag-handle__grip" aria-hidden>
              ⋮⋮
            </span>
            <span className="order-dock-drag-handle__hint">Drag</span>
          </div>
          {panel}
        </aside>
        {confirmPortal}
        {donePortal}
      </>
    );
  }

  return (
    <>
      <div className="mw-order-overlay mw-order-overlay--mt5" onClick={onClose}>
        {panel}
      </div>
      {confirmPortal}
      {donePortal}
    </>
  );
}
